"use client";
import { useEffect, useState, useCallback } from "react";
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react";
import type { ToastEvent, ToastType } from "@/lib/toast";

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  exiting: boolean;
}

const TOAST_DURATION = 5000;
const EXIT_ANIMATION_MS = 300;

const icons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles: Record<ToastType, string> = {
  success: "bg-emerald-700 text-white",
  error: "bg-red-600 text-white",
  warning: "bg-amber-500 text-white",
  info: "bg-blue-600 text-white",
};

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }, []);

  useEffect(() => {
    function handleToast(e: Event) {
      const { detail } = e as CustomEvent<ToastEvent>;
      setToasts((prev) => [...prev, { ...detail, exiting: false }]);

      setTimeout(() => {
        dismissToast(detail.id);
      }, TOAST_DURATION);
    }

    window.addEventListener("nitra-toast", handleToast);
    return () => window.removeEventListener("nitra-toast", handleToast);
  }, [dismissToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => {
        const Icon = icons[toast.type];
        return (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium min-w-[300px] max-w-[420px] transition-all duration-300 ${
              styles[toast.type]
            } ${
              toast.exiting
                ? "opacity-0 translate-x-4"
                : "opacity-100 translate-x-0"
            }`}
            style={{
              animation: toast.exiting
                ? undefined
                : "toast-slide-in 0.3s ease-out",
            }}
          >
            <Icon size={18} className="shrink-0" />
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => dismissToast(toast.id)}
              className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Dismiss notification"
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
